import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import '../../../src/styles/animations.css';
import {
  ViewColumn as SplitViewIcon,
} from '@mui/icons-material';
import visualService from '../../services/visualService';
// Import full screen functionality
import useFullScreen from '../../hooks/useFullScreen';
import FullScreenButton from '../common/FullScreenButton';
import DevicePreviewFrame from '../common/DevicePreviewFrame';
import DesignActionButtons from '../atoms/DesignActionButtons';
import ArtifactShellRenderer from '../common/ArtifactShellRenderer';

/**
 * A split-view layout component that shows designs or design system on the left (75%)
 * and conversation on the right (25%).
 *
 * Layout intentionally mirrors SplitViewWireframes — the sidebar (VisualLinksPanel,
 * rendered inside `children`) already handles the page list, page-count chip, and
 * the "Download All" button, so the left panel only needs the preview + title +
 * full-screen toggle.
 */
const SplitViewDesigns = ({ conversation, children, isStreaming, streamingContent, refreshKey = 0 }) => {
  const [sidebarCollapsed] = useState(false);
  const [designHtml, setDesignHtml] = useState('');
  const [device, setDevice] = useState('desktop'); // 'desktop' | 'tablet' | 'mobile'
  const [currentPageId, setCurrentPageId] = useState(null);
  const [contentKey, setContentKey] = useState(() => Date.now()); // Key for stable iframe rendering
  const [hasDesignsFromApi, setHasDesignsFromApi] = useState(false);
  const [isCheckingDesigns, setIsCheckingDesigns] = useState(true);

  // Fixed panel sizes - 75/25 split
  const leftPanelWidth = 0.75;
  const topPanelHeight = 0.75;

  const designPanelRef = useRef(null);
  const designContentRef = useRef('');
  const containerRef = useRef(null);

  // Add full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
  } = useFullScreen();

  // Determine if we should show the split view
  const isDesignsConversation = conversation && ((conversation?.taskId || conversation?.task_id) === 'designs' || (conversation?.taskId || conversation?.task_id) === 'design-system');
  const isDesignSystem = conversation?.taskId === 'design-system';
  const titleText = isDesignSystem ? 'Design System' : 'Designs';
  const taskTypeSlug = isDesignSystem ? 'design-system' : 'designs';

  // Check if designs exist via API when component mounts or projectId changes
  useEffect(() => {
    const checkDesignsExist = async () => {
      if (!conversation?.projectId || !isDesignsConversation) {
        setIsCheckingDesigns(false);
        return;
      }

      try {
        setIsCheckingDesigns(true);
        const result = await visualService.listPages(conversation.projectId, taskTypeSlug);
        const hasPages = result?.data?.pages?.length > 0 || result?.pages?.length > 0;
        console.log(`SplitViewDesigns: API check for ${taskTypeSlug}:`, hasPages ? 'Found' : 'Not found', result);
        setHasDesignsFromApi(hasPages);
      } catch (error) {
        console.log('SplitViewDesigns: Error checking designs (may not exist yet):', error);
        setHasDesignsFromApi(false);
      } finally {
        setIsCheckingDesigns(false);
      }
    };

    checkDesignsExist();
  }, [conversation?.projectId, isDesignsConversation, taskTypeSlug, refreshKey]);

  // Watch for conversation metadata changes (designs_generated or design_system_generated flag)
  // to force iframe re-render when designs are created via Claude Agent SDK
  useEffect(() => {
    if (conversation?.metadata?.designs_generated || conversation?.metadata?.design_system_generated) {
      setContentKey(prev => prev + 1);
      setHasDesignsFromApi(true);
      console.log('SplitViewDesigns: Designs generated via SDK, forcing iframe re-render');
    }
  }, [conversation?.metadata?.designs_generated, conversation?.metadata?.designs_url,
      conversation?.metadata?.design_system_generated, conversation?.metadata?.design_system_url]);

  // Watch for assistant messages that indicate designs were created
  // This handles the case where metadata isn't updated but the success message appears
  useEffect(() => {
    if (!conversation?.messages || !isDesignsConversation) return;

    const hasDesignSuccessMessage = conversation.messages.some(msg =>
      msg.role === 'assistant' &&
      (msg.content?.includes('Design System created successfully') ||
       msg.content?.includes('Designs created successfully') ||
       msg.content?.includes('pages generated') ||
       msg.content?.includes('files generated'))
    );

    if (hasDesignSuccessMessage && !hasDesignsFromApi) {
      console.log('SplitViewDesigns: Detected design success message, re-checking API');
      const recheckDesigns = async () => {
        try {
          const result = await visualService.listPages(conversation.projectId, taskTypeSlug);
          const hasPages = result?.data?.pages?.length > 0 || result?.pages?.length > 0;
          if (hasPages) {
            console.log(`SplitViewDesigns: Found ${taskTypeSlug} via API after success message`);
            setHasDesignsFromApi(true);
            setContentKey(prev => prev + 1);
          }
        } catch (error) {
          console.log('SplitViewDesigns: Error re-checking designs:', error);
        }
      };
      recheckDesigns();
    }
  }, [conversation?.messages, isDesignsConversation, hasDesignsFromApi, conversation?.projectId, taskTypeSlug]);

  // Also check API when streaming ends — in case the success message detection missed it
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && isDesignsConversation && !hasDesignsFromApi) {
      console.log('SplitViewDesigns: Streaming ended, checking API for designs');
      const timeoutId = setTimeout(async () => {
        try {
          const result = await visualService.listPages(conversation?.projectId, taskTypeSlug);
          const hasPages = result?.data?.pages?.length > 0 || result?.pages?.length > 0;
          if (hasPages) {
            console.log(`SplitViewDesigns: Found ${taskTypeSlug} via API after streaming ended`);
            setHasDesignsFromApi(true);
            setContentKey(prev => prev + 1);
          }
        } catch (error) {
          console.log('SplitViewDesigns: Error checking API after streaming:', error);
        }
      }, 1500); // Wait 1.5 seconds for files to be saved

      return () => clearTimeout(timeoutId);
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, isDesignsConversation, hasDesignsFromApi, conversation?.projectId, taskTypeSlug]);

  // Watch for refreshKey changes from parent (when pages are edited via VisualLinksPanel)
  useEffect(() => {
    if (refreshKey > 0) {
      const newKey = Date.now();
      setContentKey(newKey);
      console.log('SplitViewDesigns: Page edited, forcing iframe re-render (refreshKey:', refreshKey, ', newContentKey:', newKey, ')');
    }
  }, [refreshKey]);

  // Extract design HTML from streaming content or latest assistant message.
  // Used only by the legacy inline-HTML render path — the primary path is file-based.
  useEffect(() => {
    if (!isDesignsConversation) return;

    let timeoutId = null;

    const processContent = () => {
      const assistantMessages = conversation?.messages
        ?.filter(msg => msg.role === 'assistant') || [];

      const latestMessage = assistantMessages.length > 0
        ? assistantMessages[assistantMessages.length - 1].content
        : '';

      const content = isStreaming && streamingContent ? streamingContent : latestMessage;
      designContentRef.current = content;

      if (content.trim()) {
        try {
          if (content.includes('<!DOCTYPE html>') || content.includes('<html') || content.includes('<body')) {
            const htmlStartIndex = content.indexOf('<!DOCTYPE html>') >= 0
              ? content.indexOf('<!DOCTYPE html>')
              : (content.indexOf('<html') >= 0 ? content.indexOf('<html') : content.indexOf('<body'));

            let htmlEndIndex = content.lastIndexOf('</html>');
            if (htmlEndIndex >= 0) {
              htmlEndIndex += 7; // Include the closing </html> tag
            } else {
              htmlEndIndex = content.length;
            }

            const extractedHtml = content.substring(htmlStartIndex, htmlEndIndex);

            if (extractedHtml !== designHtml && extractedHtml.includes('<body')) {
              setDesignHtml(extractedHtml);
            }
          }
        } catch (error) {
          console.error('Error extracting HTML from content:', error);
        }
      }
    };

    if (isStreaming) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(processContent, 500); // Debounce during streaming
    } else {
      processContent();
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [conversation, isStreaming, streamingContent, isDesignsConversation, designHtml]);

  // Auto-scroll design panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && designPanelRef.current && isDesignsConversation) {
      setTimeout(() => {
        if (designPanelRef.current) {
          designPanelRef.current.scrollTop = designPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isDesignsConversation]);

  // Update content key when designHtml changes
  useEffect(() => {
    if (designHtml) {
      setContentKey(prevKey => prevKey + 1);
    }
  }, [designHtml]);

  // Function to add navigation protection to HTML content (legacy inline path)
  const addNavigationProtection = (htmlContent) => {
    if (!htmlContent) return htmlContent;

    if (htmlContent.includes('preventNavigationScript')) {
      return htmlContent;
    }

    if (!htmlContent.includes('<base target="_self"')) {
      htmlContent = htmlContent.replace('<head>', '<head>\n  <base target="_self">');
    }

    const screenNavigationScript = `
  <script id="screenNavigationScript">
    function showScreen(screenId) {
      document.querySelectorAll('.screen').forEach(screen => {
        screen.style.display = 'none';
      });

      const screenToShow = document.getElementById(screenId);
      if (screenToShow) {
        screenToShow.style.display = 'block';
      }

      document.querySelectorAll('.screen-nav-item').forEach(item => {
        item.classList.remove('active', 'bg-gray-200', 'font-semibold');
        item.classList.add('bg-gray-100');
      });

      const activeNavItem = document.querySelector(\`.screen-nav-item[data-screen="\${screenId}"]\`);
      if (activeNavItem) {
        activeNavItem.classList.add('active', 'bg-gray-200', 'font-semibold');
        activeNavItem.classList.remove('bg-gray-100');
      }
    }

    document.addEventListener('DOMContentLoaded', function() {
      const firstScreen = document.querySelector('.screen');
      if (firstScreen) {
        showScreen(firstScreen.id);
      }

      document.querySelectorAll('.screen-nav-item').forEach(item => {
        const screenId = item.getAttribute('data-screen');
        item.addEventListener('click', function(e) {
          e.preventDefault();
          showScreen(screenId);
        });
      });
    });
  </script>`;

    if (htmlContent.includes('</body>')) {
      return htmlContent.replace('</body>', `${screenNavigationScript}</body>`);
    }
    return htmlContent.replace('</head>', `${screenNavigationScript}</head>`);
  };

  // 3-tier fallback rendering (identical pattern to SplitViewWireframes):
  // Tier 1: shell.html + page fragments (auto-detected via API by ArtifactShellRenderer)
  // Tier 2: Full HTML files on disk via content URL (legacy)
  // Tier 3: Inline HTML extracted from conversation messages (legacy)
  const renderDesignPreview = (content, contentUrl = null) => {
    const hasApiDesigns = conversation?.metadata?.designs_generated ||
                          conversation?.metadata?.design_system_generated ||
                          hasDesignsFromApi;
    if (!content && !contentUrl && !hasApiDesigns) return null;

    const usesShell = conversation?.metadata?.shell_architecture === true;
    const legacyUrl = !usesShell && contentUrl ? contentUrl : null;
    const legacyInlineHtml = !usesShell
      ? (content ? addNavigationProtection(content) : null)
      : null;

    return (
      <DevicePreviewFrame device={device} onDeviceChange={setDevice}>
        <ArtifactShellRenderer
          projectId={conversation?.projectId}
          taskType={taskTypeSlug}
          currentPageId={currentPageId}
          contentKey={contentKey}
          onPageChange={(pageId) => setCurrentPageId(pageId)}
          legacyHtmlContent={legacyInlineHtml}
          legacyContentUrl={legacyUrl}
        />
      </DevicePreviewFrame>
    );
  };

  // Don't show the split view if this isn't a designs conversation.
  // Always show split view for designs/design-system conversations — the left panel
  // renders the placeholder message if no pages exist yet.
  if (!isDesignsConversation) {
    return <>{children}</>;
  }

  // Render the split view
  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        position: 'relative',
        mt: 4,
      }}
    >
      {/* Design preview panel - LEFT SIDE */}
      <Box
        ref={designPanelRef}
        sx={{
          ...getRightPanelStyles(sidebarCollapsed),
          width: { xs: '100%', md: isFullScreen ? '100%' : (sidebarCollapsed ? 0 : `${leftPanelWidth * 100}%`) },
          height: { xs: sidebarCollapsed ? 0 : `${topPanelHeight * 100}%`, md: '100%' },
          flexDirection: 'column',
          p: 2,
          borderTop: { xs: 'none', md: 'none' },
          backgroundColor: '#f7f7f7',
          boxSizing: 'border-box',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            height: '97%',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '8px',
          }}
        >
          {/* Header with title and full-screen toggle */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}>
            {/* Left: Title */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: '#333', fontWeight: 600 }}>
                {titleText}
              </Typography>
            </Box>

            {/* Right: View/Share + Full screen toggle */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <DesignActionButtons projectId={conversation?.projectId} taskType={taskTypeSlug} />
              <FullScreenButton
                isFullScreen={isFullScreen}
                onToggle={toggleFullScreen}
              />
            </Box>
          </Box>

          {/* Design preview area */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              height: '90%',
              position: 'relative',
            }}
            className="iframe-container"
          >
            {isCheckingDesigns ? (
              // Show loading while checking for designs
              <Box sx={{
                p: 3,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
              }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography color="text.secondary">
                  Checking for {isDesignSystem ? 'design system' : 'designs'}...
                </Typography>
              </Box>
            ) : (hasDesignsFromApi ||
                conversation?.metadata?.designs_generated ||
                conversation?.metadata?.design_system_generated) ? (
              // Display designs using DevicePreviewFrame + ArtifactShellRenderer (3-tier fallback)
              <Box sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
              }}>
                {renderDesignPreview(null, null)}
              </Box>
            ) : designHtml ? (
              // Legacy mode - show design HTML content if available
              <>
                {isStreaming && (
                  <Box className="iframe-loading" sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 10,
                  }}>
                    <CircularProgress size={32} />
                  </Box>
                )}
                {conversation &&
                 conversation.savedDocument &&
                 conversation.savedDocument.contentUrl ? (
                  renderDesignPreview(null, conversation.savedDocument.contentUrl)
                ) : (
                  renderDesignPreview(designHtml)
                )}
              </>
            ) : (
              // No designs yet - show placeholder
              <Box sx={{
                p: 3,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
              }}>
                {isStreaming ? (
                  <>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography color="text.secondary" sx={{ textAlign: 'center', width: '100%' }}>
                      Waiting for {isDesignSystem ? 'design system' : 'designs'} to be generated...
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box sx={{
                      width: 64,
                      height: 64,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '8px',
                    }}>
                      <SplitViewIcon sx={{ fontSize: 32, color: '#9e9e9e' }} />
                    </Box>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', width: '100%' }}>
                      {isDesignSystem
                        ? 'No design system pages available yet.'
                        : 'No designs available yet.'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                      {isDesignSystem
                        ? 'Design system pages will appear here after generation.'
                        : 'Designs will appear here after generation.'}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Horizontal fixed divider (mobile only) */}
      {!sidebarCollapsed && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'center',
            height: '16px',
            width: '100%',
            position: 'relative',
            backgroundColor: 'transparent',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '50%',
              height: '4px',
              backgroundColor: theme => theme.palette.divider,
              borderRadius: '2px',
            },
          }}
          aria-label="Divider between panels"
        />
      )}

      {/* Main conversation panel - RIGHT SIDE */}
      <Box sx={{
        ...getLeftPanelStyles(sidebarCollapsed),
        width: { xs: '100%', md: isFullScreen ? '0%' : (sidebarCollapsed ? '100%' : `calc(${(1 - leftPanelWidth) * 100}%)`) },
        height: { xs: sidebarCollapsed ? '100%' : `calc(${(1 - topPanelHeight) * 100}% - 16px)`, md: '100%' },
        p: { xs: 1, md: 2 },
        display: isFullScreen ? 'none' : 'block',
      }}>
        {children}
      </Box>
    </Box>
  );
};

export default SplitViewDesigns;
